import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import Link from '@material-ui/core/Link';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import PhoneIcon from '@material-ui/icons/Phone';
import MailOutlineIcon from '@material-ui/icons/MailOutline';

import NoteIcon from 'components/Icons/Note';
import RPIcon from 'components/Icons/RPIcon';
import { sqlDateToLabel } from 'helpers/helpers';
import LoadingIndicator from 'components/Loading/LoadingIndicator';
import Tooltip from 'components/Tooltip/Tooltip';

import { AccountDetailStore } from 'stores/account/accountDetailStore';
import { RootStore } from 'stores/rootStore';
import useStyles from '../styles';
import { DetailPage } from '../enums';
import { Permission } from 'helpers/permissions';
import { ViewKeys } from 'stores/userStoreKC';
import DemoModeButton from 'components/Layout/DemoModeButton';
import { EdwardColors, AccountStatus, TaxMode } from 'enums';

interface Props {
  store: AccountDetailStore;
  accountId: string;
  tabChange: Function;
  openClientView: Function;
  rootStore: RootStore;
  iqOverdue?: boolean;
}

const ClientHeader: React.FC<Props> = ({ store, accountId, tabChange, openClientView, rootStore, iqOverdue }) => {
  const { t } = useTranslation();
  const { data } = store;
  const classes = useStyles({});
  const demoMode = rootStore && !rootStore.stores.userStore.view[ViewKeys.DEMO_MODE];
  const email = data.summary!.ownerData.contactEmail.split('@');
  const tablet = useMediaQuery('@media (max-width: 1023px)', { noSsr: true });
  const mobile = useMediaQuery('(max-width:400px)', { noSsr: true });
  const notes = rootStore.stores.uiStore.currentAccountNotes;
  const iqDisabled = store.editMode; // TODO: check
  const profileDisabled =
    store.editMode ||
    store.status === AccountStatus.TO_BE_SIGNED ||
    rootStore.stores.uiStore.userMenuData.processingChange;
  const [showDetail, setShowDetail] = useState(!tablet);

  useEffect(() => {
    setShowDetail(!tablet);
  }, [tablet, setShowDetail]);

  if (!data.summary) return null;

  return (
    <>
      {demoMode ? (
        <>
          <Grid container alignItems="center" className={classes.clientHeader} spacing={0}>
            {(data.summary.ownerData.reactivationEnabled || data.summary.ownerData.resetEnabled) &&
              rootStore.stores.userStore.checkPermissions(Permission.ACCOUNT_SET_CLIENTVIEW) &&
              !store.locked && (
                <Grid item xs={12} style={{ padding: '16px' }}>
                  <Button variant="contained" color="primary" onClick={() => openClientView(true)}>
                    {t(
                      data.summary.ownerData.resetEnabled
                        ? `client_resetPortalActivation${mobile ? '_short' : ''}`
                        : `client_sendPortalActivation${mobile ? '_short' : ''}`,
                    )}
                  </Button>
                </Grid>
              )}

            <Grid item xs={12} lg={3}>
              <Typography variant="h1" className={classes.clientName}>
                {/* TODO: make a Collapse control component */}
                <IconButton
                  onClick={() => setShowDetail(!showDetail)}
                  className={`${classes.expandAction} ${showDetail ? classes.expandActionExpanded : ''}`}
                  size="medium"
                >
                  <KeyboardArrowDownIcon />
                </IconButton>

                {data.summary.ownerData.companyName
                  ? data.summary.ownerData.companyName
                  : `${data.summary.ownerData.firstName} ${data.summary.ownerData.lastName}`}

                <Tooltip title={t('clientProfile_profile') || ''}>
                  <span>
                    <IconButton onClick={() => store.setPage(DetailPage.PROFILE)} disabled={profileDisabled}>
                      <AccountCircleIcon htmlColor={profileDisabled ? '#999' : '#000'} />
                    </IconButton>
                  </span>
                </Tooltip>
                {data.summary.ownerData.riskProfile !== undefined && (
                  <Tooltip title={t('accountPlan_riskFactor') || ''}>
                    <span>
                      <IconButton onClick={() => store.setPage(DetailPage.ID)} disabled={iqDisabled}>
                        <span className={`${classes.RPIcon} rp${data.summary.ownerData.riskProfile}`}>
                          <RPIcon transparentBgr strokeWidth={0} htmlColor={iqDisabled ? '#999' : '#000'} />
                          <span>{data.summary.ownerData.riskProfile}</span>
                        </span>
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Typography>
            </Grid>

            <Collapse in={showDetail} className={classes.collapsible}>
              <Grid container item xs={12} spacing={0}>
                {data.summary.ownerData.companyName ? (
                  <Grid item container xs={12} sm={4} spacing={0} className={classes.infoBox}>
                    <Grid item xs={'auto'} sm={12} xl={'auto'}>{`${t('general_companyNumber')}: `}</Grid>
                    <Grid item className={classes.value}>
                      {data.summary.ownerData.companyNumber}
                    </Grid>
                  </Grid>
                ) : (
                  <Grid item container xs={12} sm={4} spacing={0} className={classes.infoBox}>
                    <Grid item xs={'auto'} sm={12} xl={'auto'}>{`${t(
                      `general_personalId_${data.summary.ownerData.foreignIdType}${mobile ? '_short' : ''}`,
                    )}: `}</Grid>
                    <Grid item className={classes.value}>
                      {data.summary.ownerData.foreignId || data.summary.ownerData.personalNumber}
                    </Grid>
                  </Grid>
                )}

                <Grid item container xs={12} sm={4} spacing={0} className={classes.infoBox}>
                  <Grid item xs={'auto'} sm={12} xl={'auto'}>
                    {mobile ? <PhoneIcon /> : `${t('general_phone')}: `}
                  </Grid>
                  <Grid item className={classes.value}>
                    <Link underline="always" href={`tel:${data.summary.ownerData.contactPhone}`}>
                      {data.summary.ownerData.contactPhone}
                    </Link>
                  </Grid>
                </Grid>
                <Grid item container xs={12} sm={4} spacing={0} className={classes.infoBox}>
                  <Grid item xs={'auto'} sm={12} xl={'auto'}>
                    {mobile ? <MailOutlineIcon /> : `${t('general_email')}: `}
                  </Grid>
                  <Grid item className={classes.value}>
                    <Link underline="always" href={`mailto:${data.summary.ownerData.contactEmail}`}>
                      <span>{email[0]}</span>@<span>{email[1]}</span>
                    </Link>
                  </Grid>
                </Grid>
              </Grid>
            </Collapse>
            <DemoModeButton userStore={rootStore.stores.userStore} detail />
          </Grid>

          <Grid item xs={12}>
            &nbsp;
          </Grid>
        </>
      ) : (
        <Grid container alignItems="center" style={{ position: 'relative', height: '16px' }} spacing={0}>
          <DemoModeButton userStore={rootStore.stores.userStore} detail />
        </Grid>
      )}

      <Grid container spacing={0} alignItems="center">
        <Grid item xs={12}>
          <TextField
            select={true}
            defaultValue={accountId}
            onChange={(e) => store.switchAccount(e.target.value)}
            disabled={data.summary.accountsSummary.length === 1}
            className={classes.accountSelection}
          >
            {data.summary.accountsSummary.map((account) => (
              <MenuItem key={account.accountId} value={account.accountId}>
                {account.title}
                {account.activeStatus && <> ({t(`options_activeStatus_${account.activeStatus}`)})</>}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid container item xs={12} alignItems="center" spacing={0}>
          {t('general_accountNumber')}
          <strong style={{ marginLeft: '8px' }}>{store.data.detail?.variableSymbol}</strong>
          {rootStore.stores.uiStore.currentAccountId && (
            <Tooltip
              title={t(`${notes === null ? '' : notes ? 'accountNotes_title' : 'accountNotes_add'}`) || ''}
              placement="top"
            >
              <IconButton
                size="medium"
                onClick={notes === null ? undefined : () => rootStore.stores.uiStore.openNotes()}
              >
                {notes === null ? (
                  <LoadingIndicator />
                ) : (
                  <NoteIcon
                    fontSize="small"
                    htmlColor={notes ? EdwardColors.SECONDARY : EdwardColors.GRAY_DARKER}
                    defaultFill
                    transparentBgr
                    strokeWidth={0}
                  />
                )}
              </IconButton>
            </Tooltip>
          )}
          {data.detail ? (
            <>
              {data.detail.closedFrom ? (
                <>
                  {t('accountDetail_closedSince')}:{' '}
                  {data.detail ? sqlDateToLabel(data.detail.closedFrom, 'dd.MM.yyyy') : <LoadingIndicator />}
                </>
              ) : (
                <>
                  {t('accountDetail_activeSince')}:{' '}
                  {data.detail ? sqlDateToLabel(data.detail.activeFrom, 'dd.MM.yyyy') : <LoadingIndicator />}
                </>
              )}
              <span style={{ marginLeft: '16px' }} />
              <Tooltip title={t(`accountDetail_taxMode_${store.taxMode}_tooltip`)} placement="bottom">
                <span>
                  {t('accountDetail_taxMode')}:{' '}
                  <span style={{ color: store.taxMode === TaxMode.OBH ? EdwardColors.ALERT : EdwardColors.GREEN }}>
                    {store.taxMode}
                  </span>
                </span>
              </Tooltip>
            </>
          ) : (
            <LoadingIndicator />
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default observer(ClientHeader);
